import useSWR, { useSWRConfig } from 'swr';
import useSWRMutation from 'swr/mutation';
import {
  LendingStateCode,
  RicStateSchemaContent,
  useRicStateSchemaService,
} from 'hooks/services';
import { ApiCacheKey, getKey, Resource } from './key';

export const useRicStateSchemas = () => {
  const { listStates } = useRicStateSchemaService();

  const { data, error, isLoading } = useSWR(
    getKey(Resource.RIC_STATE_SCHEMAS, {}),
    () => listStates(),
    { revalidateOnFocus: false },
  );

  return {
    stateSchemas: data ?? [],
    isLoadingStateSchemas: isLoading,
    stateSchemasError: error as Error | undefined,
  };
};

export const useRicStateSchemaRevisions = (stateCode: LendingStateCode) => {
  const { listRevisionsForState } = useRicStateSchemaService();

  const { data, error, isLoading } = useSWR(
    getKey(Resource.RIC_STATE_SCHEMA_REVISIONS, { stateCode }),
    () => listRevisionsForState({ stateCode }),
    { revalidateOnFocus: false },
  );

  return {
    revisions: data ?? [],
    isLoadingRevisions: isLoading,
    revisionsError: error as Error | undefined,
  };
};

export const useRicStateSchemaContent = (stateCode?: LendingStateCode) => {
  const { getSchemaForState } = useRicStateSchemaService();

  const { data, error, isLoading } = useSWR(
    stateCode ? getKey(Resource.RIC_STATE_SCHEMA, { stateCode }) : null,
    ({ params }: ApiCacheKey<{ stateCode: LendingStateCode }>) =>
      getSchemaForState({ stateCode: params.stateCode }),
    // Keep a partially completed form stable.
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );

  return {
    schemaContent: data?.content,
    isLoadingSchemaContent: isLoading,
    schemaContentError: error as Error | undefined,
  };
};

export const useRicStateSchema = (stateCode: LendingStateCode) => {
  const { getSchemaForState, saveSchemaForState } = useRicStateSchemaService();
  const { mutate } = useSWRConfig();
  const key = getKey(Resource.RIC_STATE_SCHEMA, { stateCode });

  const { data, error, isLoading } = useSWR(
    key,
    () => getSchemaForState({ stateCode }),
    // A revalidation overwrites the editor's form state, so only the save may
    // trigger one.
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );

  const { trigger, isMutating } = useSWRMutation(
    key,
    (_key, { arg }: { arg: { content: RicStateSchemaContent } }) =>
      saveSchemaForState({ stateCode, ...arg }),
    {
      // A save changes this state's last-edited columns on the index, and appends
      // the revision the history panel has to show.
      onSuccess: () => {
        mutate(getKey(Resource.RIC_STATE_SCHEMAS, {}));
        mutate(getKey(Resource.RIC_STATE_SCHEMA_REVISIONS, { stateCode }));
      },
    },
  );

  return {
    stateSchema: data,
    isLoadingStateSchema: isLoading,
    stateSchemaError: error as Error | undefined,
    saveStateSchema: trigger,
    isSavingStateSchema: isMutating,
  };
};
